"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { usePathname, useRouter, useSearchParams } from "@/src/lib/next-compat";
import { apiFetch } from "@/src/lib/api-fetch";
import { AlertTriangle, RefreshCw, X } from "lucide-react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import type { CostFilterOptions, CostRow, CostSummary } from "@/lib/llm-models/costs";
import {
  costFilterParams,
  hasCostFilters,
  NONE_KEY,
  pageFilters,
  parseCostFilters,
  parseCostRange,
  type CostFilters,
  type CostRange,
} from "@/lib/llm-models/cost-filters";
import CostCallsTable from "./CostCallsTable";
import { FilterCombobox, FilterListbox, focusRing } from "./CostFilterControls";
import { RangePicker, rangeLabel, rangePhrase, withRange } from "./CostRangePicker";
import { costUsd, plural, tokens } from "./format";

const BAR_COLOR = "#6366f1"; // indigo-500, the page's accent
/** Rows shown before "Show all": the task table, and the model and course lists. */
const TASK_ROWS = 12;
const LIST_ROWS = 5;

type SummaryResponse = { summary: CostSummary; options: CostFilterOptions };

async function getJson<T>(url: string, what: string): Promise<T> {
  const res = await apiFetch(url);
  if (res.status === 401) throw new Error("Your session has expired. Sign in again.");
  if (res.status === 403) throw new Error("Only inaraX admins can see AI costs.");
  if (res.status === 400) throw new Error(((await res.json().catch(() => ({}))) as { error?: string }).error ?? `Failed to load ${what}.`);
  if (!res.ok) throw new Error(`Failed to load ${what}.`);
  return (await res.json()) as T;
}

/**
 * Chart keys are Pakistan wall-clock times ("2026-10-19" or "2026-10-19T14:00"). Read as UTC and
 * printed in UTC, they come out exactly as written, whatever the viewer's own time zone.
 */
function wallClock(key: string): Date {
  return new Date(key.length === 10 ? `${key}T00:00:00Z` : `${key}:00Z`);
}

/** A short axis label: "Oct 19" or "2 PM". */
function pointLabel(key: string, granularity: CostSummary["granularity"]): string {
  return granularity === "day"
    ? wallClock(key).toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone: "UTC" })
    : wallClock(key).toLocaleTimeString(undefined, { hour: "numeric", timeZone: "UTC" });
}

/** The same point in full, for the tooltip: "Oct 19, 2026" or "Oct 19, 2 PM". */
function pointTitle(key: string, granularity: CostSummary["granularity"]): string {
  return granularity === "day"
    ? wallClock(key).toLocaleDateString(undefined, { dateStyle: "medium", timeZone: "UTC" })
    : wallClock(key).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", timeZone: "UTC" });
}

function share(cost: string, total: number): number {
  return total > 0 ? Math.round((Number(cost) / total) * 100) : 0;
}

/**
 * A breakdown's own total. Each breakdown ignores its own filter (so the other rows stay
 * visible), which makes it differ from the page total whenever that filter is set.
 */
function rowsTotal(rows: CostRow[]): number {
  return rows.reduce((sum, r) => sum + Number(r.cost), 0);
}

function Card({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <section className="rounded-xl bg-white shadow-sm ring-1 ring-slate-200">
      <div className="px-5 pt-4">
        <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
        {hint ? <p className="mt-0.5 text-xs text-slate-500">{hint}</p> : null}
      </div>
      {children}
    </section>
  );
}

function Tile({ label, value, detail, icon }: { label: string; value: string; detail?: string; icon?: React.ReactNode }) {
  return (
    <>
      <p className="flex items-center gap-1.5 text-xs font-medium text-slate-500">
        {icon}
        {label}
      </p>
      <p className="mt-1 text-2xl font-bold tabular-nums tracking-tight text-slate-900">{value}</p>
      {detail ? <p className="mt-0.5 text-xs text-slate-500">{detail}</p> : null}
    </>
  );
}

function SpendChart({ summary }: { summary: CostSummary }) {
  // Keyed by the timestamp: hourly labels repeat across two days, and a repeated category collapses.
  const data = summary.series.map((p) => ({ time: p.time, label: pointTitle(p.time, summary.granularity), spend: Number(p.cost), cost: p.cost, calls: p.calls }));
  const peak = data.reduce((best, p) => (p.spend > best.spend ? p : best), data[0]);
  return (
    <Card title={summary.granularity === "hour" ? "Spend per hour" : "Spend per day"} hint="Pakistan time">
      <p className="sr-only">{peak && peak.spend > 0 ? `Highest: ${peak.label}, ${costUsd(peak.cost)}.` : "No spend in this period."}</p>
      <div className="h-56 w-full px-2 pb-3 pt-2" aria-hidden="true">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 8, right: 12, left: 4, bottom: 0 }}>
            <CartesianGrid vertical={false} stroke="#E2E8F0" />
            <XAxis
              dataKey="time"
              tickFormatter={(t: string) => pointLabel(t, summary.granularity)}
              axisLine={false}
              tickLine={false}
              tick={{ fill: "#64748B", fontSize: 10 }}
              minTickGap={16}
            />
            <YAxis axisLine={false} tickLine={false} tick={{ fill: "#64748B", fontSize: 10 }} width={64} tickFormatter={(v: number) => costUsd(String(v))} />
            <Tooltip
              cursor={{ fill: "#EEF2FF" }}
              isAnimationActive={false}
              content={({ active, payload }) => {
                const p = active ? (payload?.[0]?.payload as (typeof data)[number] | undefined) : undefined;
                if (!p) return null;
                return (
                  <div className="rounded-lg bg-slate-800 px-3 py-2 text-xs text-white shadow-lg">
                    <p className="font-semibold">{p.label}</p>
                    <p className="mt-0.5 tabular-nums">
                      {costUsd(p.cost)} · {plural(p.calls, "call")}
                    </p>
                  </div>
                );
              }}
            />
            <Bar dataKey="spend" fill={BAR_COLOR} radius={[4, 4, 0, 0]} maxBarSize={28} isAnimationActive={false} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}

function ShareBar({ cost, max }: { cost: string; max: number }) {
  return (
    <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-slate-100">
      <div className="h-full rounded-full" style={{ width: `${max > 0 ? (Number(cost) / max) * 100 : 0}%`, backgroundColor: BAR_COLOR }} />
    </div>
  );
}

function ShowAll({ total, limit, expanded, onToggle }: { total: number; limit: number; expanded: boolean; onToggle: () => void }) {
  if (total <= limit) return null;
  return (
    <button type="button" onClick={onToggle} className={`rounded px-2 text-xs font-semibold text-indigo-600 hover:text-indigo-800 ${focusRing}`}>
      {expanded ? "Show fewer" : `Show all ${total}`}
    </button>
  );
}

type BreakdownProps = {
  rows: CostRow[];
  selected: string | undefined;
  onSelect: (key: string | undefined) => void;
};

/**
 * A breakdown as a table, for the dimension that keeps growing (tasks). Selecting a row filters
 * the whole tab to it; selecting it again clears that filter.
 */
function SpendTable({ title, firstColumn, rows, selected, onSelect, limit }: BreakdownProps & { title: string; firstColumn: string; limit: number }) {
  const [expanded, setExpanded] = useState(false);
  const total = rowsTotal(rows);
  const max = Math.max(0, ...rows.map((r) => Number(r.cost)));
  const visible = expanded ? rows : rows.slice(0, limit);
  return (
    <Card title={title} hint="Select a row to filter by it.">
      {rows.length === 0 ? (
        <p className="px-5 pb-5 pt-3 text-sm text-slate-500">No calls.</p>
      ) : (
        <div className="pb-3 pt-3">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[40rem] text-left text-sm">
              <thead>
                <tr className="whitespace-nowrap border-y border-slate-100 bg-slate-50/60 text-xs font-semibold text-slate-500">
                  <th className="px-5 py-2 font-semibold">{firstColumn}</th>
                  <th className="px-3 py-2 text-right font-semibold">Calls</th>
                  <th className="px-3 py-2 text-right font-semibold">Input tokens</th>
                  <th className="px-3 py-2 text-right font-semibold">Output tokens</th>
                  <th className="px-3 py-2 text-right font-semibold">Spend</th>
                  <th className="w-[22%] px-5 py-2 font-semibold">Share of spend</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((r) => {
                  const isSelected = selected === r.key;
                  return (
                    <tr
                      key={r.key}
                      onClick={() => onSelect(isSelected ? undefined : r.key)}
                      className={`cursor-pointer border-t border-slate-100 hover:bg-slate-50 ${isSelected ? "bg-indigo-50 hover:bg-indigo-50" : ""}`}
                    >
                      <td className="max-w-[18rem] px-5 py-2">
                        <button
                          type="button"
                          aria-pressed={isSelected}
                          onClick={(e) => {
                            e.stopPropagation();
                            onSelect(isSelected ? undefined : r.key);
                          }}
                          className={`max-w-full truncate rounded text-left font-medium ${isSelected ? "text-indigo-700" : "text-slate-800"} ${focusRing}`}
                          title={r.label}
                        >
                          {r.label}
                        </button>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-700">{r.calls.toLocaleString()}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-700" title={r.tokensIn.toLocaleString()}>
                        {tokens(r.tokensIn)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-700" title={r.tokensOut.toLocaleString()}>
                        {tokens(r.tokensOut)}
                      </td>
                      <td className="px-3 py-2 text-right font-semibold tabular-nums text-slate-900">{costUsd(r.cost)}</td>
                      <td className="px-5 py-2">
                        <div className="flex items-center gap-2">
                          <ShareBar cost={r.cost} max={max} />
                          <span className="w-9 text-right text-xs tabular-nums text-slate-500">{share(r.cost, total)}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="px-3 pt-2">
            <ShowAll total={rows.length} limit={limit} expanded={expanded} onToggle={() => setExpanded((v) => !v)} />
          </div>
        </div>
      )}
    </Card>
  );
}

/** A short breakdown (model, course) as bar rows; selecting one filters the tab to it. */
function BreakdownList({ title, rows, selected, onSelect }: BreakdownProps & { title: string }) {
  const [expanded, setExpanded] = useState(false);
  const total = rowsTotal(rows);
  const max = Math.max(0, ...rows.map((r) => Number(r.cost)));
  const visible = expanded ? rows : rows.slice(0, LIST_ROWS);
  return (
    <Card title={title} hint="Select a row to filter by it.">
      {rows.length === 0 ? (
        <p className="px-5 pb-5 pt-3 text-sm text-slate-500">No calls.</p>
      ) : (
        <div className="px-3 pb-3 pt-2">
          {visible.map((r) => {
            const isSelected = selected === r.key;
            return (
              <button
                key={r.key}
                type="button"
                onClick={() => onSelect(isSelected ? undefined : r.key)}
                aria-pressed={isSelected}
                className={`block w-full rounded-lg px-2 py-2 text-left transition hover:bg-slate-50 ${focusRing} ${
                  isSelected ? "bg-indigo-50 ring-1 ring-inset ring-indigo-200 hover:bg-indigo-50" : ""
                }`}
              >
                <span className="flex items-baseline justify-between gap-3 text-sm">
                  <span className={`min-w-0 break-words ${isSelected ? "font-medium text-indigo-700" : "text-slate-700"}`}>{r.label}</span>
                  <span className="shrink-0 font-semibold tabular-nums text-slate-900">{costUsd(r.cost)}</span>
                </span>
                <span className="mt-1 flex items-center gap-3">
                  <ShareBar cost={r.cost} max={max} />
                  <span className="shrink-0 text-right text-xs tabular-nums text-slate-500">
                    {plural(r.calls, "call")} · {share(r.cost, total)}% of spend
                  </span>
                </span>
              </button>
            );
          })}
          <ShowAll total={rows.length} limit={LIST_ROWS} expanded={expanded} onToggle={() => setExpanded((v) => !v)} />
        </div>
      )}
    </Card>
  );
}

/** Total calls and tokens, then the same for each model. */
function CallsByModel({ totals, rows }: { totals: CostSummary["totals"]; rows: CostRow[] }) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? rows : rows.slice(0, LIST_ROWS);
  return (
    <div className={`${tileClass} sm:col-span-2`}>
      <div className="flex flex-wrap gap-x-8 gap-y-3">
        <div>
          <Tile label="AI calls" value={totals.calls.toLocaleString()} />
        </div>
        <div title={totals.tokensIn.toLocaleString()}>
          <Tile label="Input tokens" value={tokens(totals.tokensIn)} />
        </div>
        <div title={totals.tokensOut.toLocaleString()}>
          <Tile label="Output tokens" value={tokens(totals.tokensOut)} />
        </div>
      </div>
      {rows.length > 0 ? (
        <>
          <table className="mt-3 w-full text-left text-xs">
            <thead>
              <tr className="text-slate-500">
                <th className="w-full py-1 pr-3 font-medium">Model</th>
                <th className="whitespace-nowrap px-2 py-1 text-right font-medium">Calls</th>
                <th className="whitespace-nowrap px-2 py-1 text-right font-medium">Input</th>
                <th className="whitespace-nowrap py-1 pl-2 text-right font-medium">Output</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((r) => (
                <tr key={r.key} className="border-t border-slate-100">
                  <td className="max-w-0 truncate py-1.5 pr-3 text-slate-700" title={r.label}>
                    {r.label}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-slate-700">{r.calls.toLocaleString()}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-slate-700" title={r.tokensIn.toLocaleString()}>
                    {tokens(r.tokensIn)}
                  </td>
                  <td className="py-1.5 pl-2 text-right tabular-nums text-slate-700" title={r.tokensOut.toLocaleString()}>
                    {tokens(r.tokensOut)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="-mx-2 pt-1">
            <ShowAll total={rows.length} limit={LIST_ROWS} expanded={expanded} onToggle={() => setExpanded((v) => !v)} />
          </div>
        </>
      ) : null}
    </div>
  );
}

function Chip({ label, onRemove }: { label: string; onRemove: () => void }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-indigo-50 py-0.5 pl-2.5 pr-1 text-xs font-medium text-indigo-800 ring-1 ring-inset ring-indigo-200">
      {label}
      <button type="button" aria-label={`Remove ${label}`} onClick={onRemove} className={`rounded-full p-0.5 hover:bg-indigo-100 ${focusRing}`}>
        <X className="h-3 w-3" />
      </button>
    </span>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-5" aria-busy="true">
      <span className="sr-only">Loading AI costs…</span>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="h-24 animate-pulse rounded-xl bg-white shadow-sm ring-1 ring-slate-200" />
        ))}
      </div>
      <div className="h-64 animate-pulse rounded-xl bg-white shadow-sm ring-1 ring-slate-200" />
    </div>
  );
}
const tileClass = "rounded-xl bg-white px-5 py-4 text-left shadow-sm ring-1 ring-slate-200";

export default function AiCostsClient() {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const callsRef = useRef<HTMLElement>(null);
  const topRef = useRef<HTMLDivElement>(null);

  // The URL holds the range and filters, so a filtered view can be shared or reloaded.
  const query = searchParams.toString();
  const { range, filters } = useMemo(() => {
    const params = new URLSearchParams(query);
    return { range: parseCostRange(params).range, filters: parseCostFilters(params).filters };
  }, [query]);
  // Task, course and model narrow the whole tab; status and search narrow only the call list.
  const pageOnly = useMemo<CostFilters>(() => pageFilters(filters), [filters]);
  const apiQuery = useMemo(() => withRange(costFilterParams(pageOnly), range).toString(), [range, pageOnly]);
  const listQuery = useMemo(() => withRange(costFilterParams(filters), range).toString(), [range, filters]);

  const [result, setResult] = useState<{ query: string; data: SummaryResponse | null; error: string | null } | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    getJson<SummaryResponse>(`/api/admin/ai-models/costs?${apiQuery}`, "AI costs")
      .then((data) => {
        if (!cancelled) setResult({ query: apiQuery, data, error: null });
      })
      .catch((e: unknown) => {
        if (!cancelled) setResult({ query: apiQuery, data: null, error: e instanceof Error ? e.message : "Failed to load AI costs." });
      });
    return () => {
      cancelled = true;
    };
  }, [apiQuery, attempt]);

  // Keep showing the last result while the next one loads, so the page doesn't jump on every change.
  const [shown, setShown] = useState<SummaryResponse | null>(null);
  if (result?.data && result.data !== shown) setShown(result.data);
  const loading = result?.query !== apiQuery;
  const error = !loading ? (result?.error ?? null) : null;

  const navigate = (nextRange: CostRange, nextFilters: CostFilters) => {
    const params = costFilterParams(nextFilters);
    params.set("tab", "costs");
    // The default 30 days stays out of the URL.
    if (!("period" in nextRange && nextRange.period === "30d")) withRange(params, nextRange);
    router.replace(`${pathname}?${params}`, { scroll: false });
  };
  const setFilter = (patch: Partial<CostFilters>) => navigate(range, { ...filters, ...patch });
  // Offered when nothing is found: only a preset shorter than 90 days can be widened to it.
  const canWiden = "period" in range && range.period !== "90d";

  const summary = shown?.summary ?? null;
  const options = shown?.options ?? null;
  const filtered = hasCostFilters(pageOnly);
  // The model breakdown ignores the model filter; the calls card shows just that model when one is set.
  const modelRows = summary ? (filters.model ? summary.byModel.filter((r) => r.key === filters.model) : summary.byModel) : [];

  const selectedCourse = filters.course === undefined ? undefined : String(filters.course);
  const toCourse = (key: string | undefined) => (key === undefined ? undefined : key === NONE_KEY ? NONE_KEY : Number(key));

  const selectedOrg = filters.organization === undefined ? undefined : String(filters.organization);
  const toOrg = (key: string | undefined) => (key === undefined ? undefined : key === NONE_KEY ? NONE_KEY : Number(key));

  // Chips name each active filter by its label, so a row selected below the fold is still visible here.
  const labelOf = (list: { key: string; label: string }[] | undefined, key: string) => list?.find((o) => o.key === key)?.label ?? key;
  const chips: { label: string; remove: Partial<CostFilters> }[] = [];
  if (filters.task) chips.push({ label: `Task: ${labelOf(options?.tasks, filters.task)}`, remove: { task: undefined } });
  if (selectedCourse) chips.push({ label: `Course: ${labelOf(options?.courses, selectedCourse)}`, remove: { course: undefined } });
  if (selectedOrg) chips.push({ label: `Organization: ${labelOf(options?.organizations, selectedOrg)}`, remove: { organization: undefined } });
  if (filters.model) chips.push({ label: `Model: ${labelOf(options?.models, filters.model)}`, remove: { model: undefined } });
  if (filters.learner) chips.push({ label: `Learner: ${labelOf(options?.learners, filters.learner)}`, remove: { learner: undefined } });

  return (
    <div className="space-y-5">
      <div ref={topRef} className="scroll-mt-36 space-y-4 rounded-xl bg-white p-4 shadow-sm ring-1 ring-slate-200">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <RangePicker range={range} onChange={(next) => navigate(next, filters)} />
          {filtered ? (
            <button type="button" onClick={() => navigate(range, { status: filters.status, search: filters.search })} className={`inline-flex items-center gap-1 rounded text-sm font-medium text-indigo-600 hover:text-indigo-800 ${focusRing}`}>
              <X className="h-3.5 w-3.5" /> Clear filters
            </button>
          ) : null}
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
          <FilterListbox label="Task" value={filters.task ?? ""} onChange={(v) => setFilter({ task: v || undefined })} options={options?.tasks ?? []} />
          <FilterListbox label="Course" value={selectedCourse ?? ""} onChange={(v) => setFilter({ course: toCourse(v || undefined) })} options={options?.courses ?? []} />
          <FilterListbox label="Organization" value={selectedOrg ?? ""} onChange={(v) => setFilter({ organization: toOrg(v || undefined) })} options={options?.organizations ?? []} />
          <FilterListbox label="Model" value={filters.model ?? ""} onChange={(v) => setFilter({ model: v || undefined })} options={options?.models ?? []} />
          <FilterCombobox
            label="Learner"
            placeholder="All (type an email)"
            value={filters.learner ?? ""}
            onChange={(v) => setFilter({ learner: v || undefined })}
            options={options?.learners ?? []}
          />
        </div>
        {chips.length > 0 ? (
          <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3">
            <span className="text-xs font-medium text-slate-500">Showing only</span>
            {chips.map((c) => (
              <Chip key={c.label} label={c.label} onRemove={() => setFilter(c.remove)} />
            ))}
          </div>
        ) : null}
      </div>

      {error && !summary ? (
        <div className="rounded-xl bg-white p-8 text-center shadow-sm ring-1 ring-slate-200">
          <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-red-50 text-red-600">
            <AlertTriangle className="h-5 w-5" />
          </div>
          <h2 className="mt-3 text-base font-semibold text-slate-900">Could not load AI costs</h2>
          <p className="mx-auto mt-1 max-w-md text-sm text-slate-500">{error}</p>
          <button
            type="button"
            onClick={() => {
              // Drop the failed result so the page shows it's loading again.
              setResult(null);
              setAttempt((n) => n + 1);
            }}
            className={`mt-4 inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 ${focusRing}`}
          >
            <RefreshCw className="h-3.5 w-3.5" /> Try again
          </button>
        </div>
      ) : !summary ? (
        <LoadingSkeleton />
      ) : (
        <div className={`space-y-5 transition-opacity ${loading ? "opacity-60" : ""}`} aria-busy={loading}>
          {error ? (
            <div className="flex flex-wrap items-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-inset ring-red-200">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              <span className="min-w-0 flex-1">{error} The numbers below are from before this change.</span>
              <button
                type="button"
                onClick={() => {
                  setResult(null);
                  setAttempt((n) => n + 1);
                }}
                className={`inline-flex items-center gap-1.5 rounded-lg bg-white px-3 py-1.5 text-sm font-medium text-red-700 shadow-sm ring-1 ring-red-200 hover:bg-red-50 ${focusRing}`}
              >
                <RefreshCw className="h-3.5 w-3.5" /> Try again
              </button>
            </div>
          ) : null}

          {summary.totals.calls === 0 ? (
            <div className="rounded-xl bg-white p-8 text-center shadow-sm ring-1 ring-slate-200">
              <h2 className="text-base font-semibold text-slate-900">
                {filtered ? "No calls match these filters." : `No AI calls ${rangePhrase(range)}.`}
              </h2>
              <div className="mt-4 flex flex-wrap justify-center gap-2">
                {canWiden && !filtered ? (
                  <button
                    type="button"
                    onClick={() => navigate({ period: "90d" }, filters)}
                    className={`rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 ${focusRing}`}
                  >
                    Look in the last 90 days
                  </button>
                ) : null}
                {filtered ? (
                  <button
                    type="button"
                    onClick={() => navigate(range, { status: filters.status, search: filters.search })}
                    className={`rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 ${focusRing}`}
                  >
                    Clear filters
                  </button>
                ) : null}
              </div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div className={tileClass}>
                  <Tile label="Spend" value={costUsd(summary.totals.cost)} detail={rangeLabel(range)} />
                </div>
                <CallsByModel totals={summary.totals} rows={modelRows} />
              </div>

              <SpendChart summary={summary} />

              <SpendTable
                title="Spend by task"
                firstColumn="Task"
                rows={summary.byTask}
                limit={TASK_ROWS}
                selected={filters.task}
                onSelect={(task) => setFilter({ task })}
              />

              <div className="grid gap-5 lg:grid-cols-3">
                <BreakdownList title="Spend by model" rows={summary.byModel} selected={filters.model} onSelect={(model) => setFilter({ model })} />
                <BreakdownList
                  title="Spend by course"
                  rows={summary.byCourse}
                  selected={selectedCourse}
                  onSelect={(key) => setFilter({ course: toCourse(key) })}
                />
                <BreakdownList
                  title="Spend by organization (B2B)"
                  rows={summary.byOrganization ?? []}
                  selected={selectedOrg}
                  onSelect={(key) => setFilter({ organization: toOrg(key) })}
                />
              </div>

            </>
          )}

          {summary.totals.calls > 0 ? (
            <CostCallsTable
              ref={callsRef}
              key={listQuery}
              range={range}
              filters={filters}
              onStatus={(status) => setFilter({ status })}
              onSearch={(search) => setFilter({ search })}
              onLearner={(learner) => {
                // Their totals, chart and courses are at the top of the tab.
                setFilter({ learner, search: undefined });
                topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
              onWiden={canWiden ? () => navigate({ period: "90d" }, filters) : null}
            />
          ) : null}
        </div>
      )}
    </div>
  );
}
